type ContentfulFit = 'fill' | 'pad' | 'crop' | 'thumb' | 'scale';
type ContentfulFormat = 'jpg' | 'webp' | 'png';

interface BuildContentfulImageUrlOptions {
  url: string;
  w: number;
  h?: number;
  q?: number;
  fit?: ContentfulFit;
  fm?: ContentfulFormat;
}

export const buildContentfulImageUrl = ({
  url,
  w,
  h,
  q = 85,
  fit,
  fm = 'jpg',
}: BuildContentfulImageUrlOptions): string => {
  const params = new URLSearchParams();
  params.set('w', String(w));
  if (h !== undefined) params.set('h', String(h));
  params.set('q', String(q));
  if (fit) params.set('fit', fit);
  params.set('fm', fm);
  return `${url}?${params.toString()}`;
};

interface BuildContentfulImageSourcesOptions {
  url: string;
  widths: number[];
  q?: number;
  fit?: ContentfulFit;
  aspectRatio?: { w: number; h: number };
  defaultWidth?: number;
}

export interface ContentfulImageSources {
  src: string;
  srcSet: string;
  webpSrcSet: string;
}

export const buildContentfulImageSources = ({
  url,
  widths,
  q,
  fit,
  aspectRatio,
  defaultWidth,
}: BuildContentfulImageSourcesOptions): ContentfulImageSources => {
  const heightFor = (w: number): number | undefined =>
    aspectRatio ? Math.round((w * aspectRatio.h) / aspectRatio.w) : undefined;

  const srcWidth = defaultWidth ?? widths[widths.length - 1];

  const src = buildContentfulImageUrl({
    url,
    w: srcWidth,
    h: heightFor(srcWidth),
    q,
    fit,
    fm: 'jpg',
  });

  const buildSet = (fm: ContentfulFormat) =>
    widths
      .map((w) => `${buildContentfulImageUrl({ url, w, h: heightFor(w), q, fit, fm })} ${w}w`)
      .join(', ');

  return {
    src,
    srcSet: buildSet('jpg'),
    webpSrcSet: buildSet('webp'),
  };
};
